import { Buffer } from 'buffer';
import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import SupportEmailLink from '@/components/common/SupportEmailLink';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { getBulkImport } from '@/lib/services/bulkImportServices';
import { downloadFile, UploadContexts } from '@/modules/FileUploader';
import { I18nStrong } from '@/modules/I18n';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import BulkImportStatus from '../../config/BulkImportStatus';
import showProcessedStats from '../../config/showProcessedStats';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import useUpdateBulkImport from '../../hooks/useUpdateBulkImport';
import type BulkImport from '../../types/BulkImport';
import type ProcessedStats from '../../types/ProcessedStats';
import CancelDataUploadButton from './CancelDataUploadButton';

function DataOverviewMessage(fileName: string) {
  return translate('DATA_UPLOADS_DATA_OVERVIEW_MESSAGE', {
    fileName,
    strong: I18nStrong,
  });
}

function WarningMessage() {
  return translate('DATA_UPLOADS_REVIEW_CONTACT', {
    a: SupportEmailLink,
  });
}

function SuccessMessage({
  numSuccess,
  numTotalRows,
}: {
  numSuccess: number;
  numTotalRows: number;
}) {
  return translate('DATA_UPLOADS_SUCCESS_COUNT', {
    div1: (chunks: React.ReactNode) => (
      <div
        className="text-2xl font-medium"
        data-testid="data-upload-success-count"
      >
        {chunks}
      </div>
    ),
    div2: (chunks: React.ReactNode) => <div>{chunks}</div>,
    numSuccess,
    numTotalRows,
  });
}

export function DataSummary({
  numCreated,
  numErrors,
  numSkipped,
  numSuccess,
  numTotalRows,
  numUpdated,
  statName,
}: {
  numCreated: number;
  numErrors: number;
  numSkipped: number;
  numSuccess: number;
  numTotalRows: number;
  numUpdated?: number;
  statName: string;
}) {
  return (
    <div className="flex grow flex-col gap-2 bg-grey-100 p-4">
      <div>{translate(statName)}</div>
      <div className="h-[2px] w-[48px] bg-grey-600" />
      <div>
        <SuccessMessage {...{ numSuccess, numTotalRows, numErrors }} />
      </div>
      <div className="text-xs text-text-secondary">
        {translate('DATA_UPLOADS_NUM_ERRORS', {
          numErrors,
        })}
        <Tooltip title={translate('DATA_UPLOADS_ERROR_TOOLTIP')}>
          <InfoOutlinedIcon className="ml-1 text-base" />
        </Tooltip>
        {' | '}
        {translate('DATA_UPLOADS_NUM_SKIPPED', {
          numSkipped,
        })}
        <Tooltip title={translate('DATA_UPLOADS_SKIPPED_TOOLTIP')}>
          <InfoOutlinedIcon className="ml-1 text-base" />
        </Tooltip>
      </div>
      <div className="text-xs text-text-secondary">
        {translate('DATA_UPLOADS_NUM_CREATED', {
          numCreated,
        })}
        {Boolean(numUpdated) && (
          <>
            {' | '}
            {translate('DATA_UPLOADS_NUM_UPDATED', {
              numUpdated,
            })}
          </>
        )}
      </div>
    </div>
  );
}

export type DataReviewSummaryProps = {
  data: BulkImport;
};

export default function DataReviewSummary({ data }: DataReviewSummaryProps) {
  const {
    bulkImportId,
    inputFileName,
    processedStats = {} as ProcessedStats,
    preProcessedStats = {} as ProcessedStats,
    status,
  } = data;

  const updateBulkImport = useUpdateBulkImport();
  const ownerOrgId = useOwnerOrgId();

  const summaryStats = React.useMemo(() => {
    const propertyData = showProcessedStats.includes(status)
      ? processedStats
      : preProcessedStats;

    if (!Object.keys(propertyData).length) {
      return [];
    }

    return [
      {
        statName: 'DATA_UPLOADS_CAMPAIGNS',
        numSuccess: propertyData.campaigns.succeeded,
        numTotalRows: propertyData.campaigns.records,
        numErrors: propertyData.campaigns.failed,
        numSkipped: propertyData.campaigns.skipped,
        numCreated: propertyData.campaigns.created,
      },
      {
        statName: 'DATA_UPLOADS_CONTRIBUTIONS',
        numSuccess: propertyData.donations.succeeded,
        numTotalRows: propertyData.donations.records,
        numErrors: propertyData.donations.failed,
        numSkipped: propertyData.donations.skipped,
        numCreated: propertyData.donations.created,
      },
      {
        statName: 'DATA_UPLOADS_CONSTITUENTS',
        numSuccess: propertyData.constituents.succeeded,
        numTotalRows: propertyData.constituents.records,
        numErrors: propertyData.constituents.failed,
        numSkipped: propertyData.constituents.skipped,
        numCreated: propertyData.constituents.created,
        numUpdated: propertyData.constituents.updated,
      },
    ];
  }, [status, processedStats, preProcessedStats]);

  const handleErrorsCsvClick = React.useCallback(async () => {
    if (!bulkImportId) {
      throw new Error(translate('DOWNLOADS_ERROR'));
    }

    try {
      const results = await getBulkImport(bulkImportId);
      const { errorsS3Path } = results.data.bulkImport;

      if (!errorsS3Path) {
        throw new Error(translate('DOWNLOADS_ERROR'));
      }

      await downloadFile(
        {
          ownerOrgId,
          context: UploadContexts.IMPORTS,
          uploadId: Buffer.from(errorsS3Path).toString('base64'),
        },
        errorsS3Path,
      );
    } catch (e) {
      showErrorMessage({
        message: translate('DOWNLOADS_ERROR'),
      });
    }
  }, [bulkImportId]);

  return (
    <div data-testid="data-review-summary">
      <h2 className="type-h2">{translate('DATA_UPLOADS_DATA_OVERVIEW')}</h2>
      {showProcessedStats.includes(status)
        ? translate('DATA_UPLOADS_DATA_OVERVIEW_PROCESS_COMPLETE_MESSAGE')
        : DataOverviewMessage(inputFileName)}
      <div className="space-between flex flex-col gap-4 md:flex-row">
        {summaryStats.map((stat) => (
          <DataSummary key={stat.statName} {...stat} />
        ))}
      </div>
      <div className="flex flex-col gap-4 pt-4">
        <h2 className="type-h2">{translate('DATA_UPLOADS_ERROR_SUMMARY')}</h2>
        {translate('DATA_UPLOADS_ERROR_SUMMARY_MESSAGE', {
          num: showProcessedStats
            ? processedStats?.totals?.failed
            : preProcessedStats?.totals?.failed,
        })}
        <Button
          className="justify-start p-0"
          data-testid="download-error-summary"
          onClick={handleErrorsCsvClick}
          variant="text"
        >
          {translate('DATA_UPLOADS_DOWNLOAD_ERROR_REPORT_CSV')}
        </Button>
      </div>
      {status === BulkImportStatus.DATA_PRE_PROCESSED && (
        <>
          <Alert
            className="text-center"
            classes={{ message: 'w-full' }}
            icon={false}
            severity="warning"
          >
            <div className="font-bold type-body2">
              {translate('DATA_UPLOADS_REVIEW_WARNING')}
            </div>
            <div className="type-body2">
              <WarningMessage />
            </div>
          </Alert>
          <div className="flex flex-col gap-6 pt-4 text-center">
            <div className="text-center">
              <Button
                data-testid="data-review-confirm-button"
                onClick={() => {
                  updateBulkImport({
                    bulkImportId,
                    status: BulkImportStatus.READY_TO_PROCESS,
                  });
                }}
                variant="contained"
              >
                {translate('DATA_UPLOADS_FINISH_IMPORT')}
              </Button>
              <br />
              <CancelDataUploadButton
                bulkImportId={bulkImportId}
                className="mt-1"
                data-testid="data-review-cancel-button"
              >
                {translate('DATA_UPLOADS_CANCEL_IMPORT')}
              </CancelDataUploadButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
